/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormulaContext, FormulaTestEvent } from '@gh/builder-shared';
import { Formula, isFormulaEmpty } from '@gh/builder-shared-data';
import { getRouteInitData, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { CellDoubleClickedEvent } from 'ag-grid-community';
import { isEmpty } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import {
  getJeComponentId,
  JeComponent,
  JeItem,
} from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import {
  JeDetailsState,
  queryJeComponentItems,
  queryJeEditable,
  queryJeSelectedComponent,
  queryJeSelectedComponentId,
  queryJeSelectedDetailsTab,
  queryJeSelectionHighlightInvalid,
} from '../je-details.state';
import { JeDetailsComponentItemGridDefinition } from './je-details-component-item-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-je-details-component-details',
  templateUrl: 'je-details-component-details.component.html',
  styleUrls: ['je-details-component-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [JeDetailsComponentItemGridDefinition],
})
export class JeDetailsComponentDetailsComponent implements OnInit, OnDestroy {
  items$: Observable<JeItem[]>;
  formula$: Observable<Maybe<Formula>>;
  selectionIdentity$: Observable<Maybe<ID>>;
  memo$: Observable<string>;
  total$: Observable<number>;
  selectedTab$: Observable<string>;
  formulaEditable$: Observable<boolean>;
  highlightInvalid$: Observable<boolean>;
  isFormulaEmpty$: Observable<boolean>;

  editable$: Observable<boolean>;

  contextItem: JeItem;

  formulaContext: FormulaContext;

  componentId: ID;
  component: JeComponent;
  private componentSubscription: Subscription;

  constructor(public route: ActivatedRoute,
              public itemGridDefinition: JeDetailsComponentItemGridDefinition,
              private state: State<JeDetailsState>,
              private commands: JeCommandService) {

  }

  ngOnInit(): void {
    const { formulaContext } = getRouteInitData(this.route);

    this.formulaContext = formulaContext;

    const { state } = this;
    const componentId$ = state.select(queryJeSelectedComponentId).pipe(filter$(Boolean));
    const component$ = state.select(queryJeSelectedComponent).pipe(filter$(Boolean));

    this.items$ = componentId$.pipe(switchMap$((id) => state.select(queryJeComponentItems, id)));
    this.formula$ = component$.pipe(map$((component: any) => component.formula));
    this.selectionIdentity$ = state.select(queryJeSelectedComponentId);
    this.memo$ = component$.pipe(map$((component: any) => component.memo));
    this.total$ = component$.pipe(map$((component: any) => component.cost || 0));
    this.selectedTab$ = state.select(queryJeSelectedDetailsTab).pipe(filter$(Boolean));
    this.formulaEditable$ = component$.pipe(map$((component: any) => isEmpty(component.cuttingList)));
    this.highlightInvalid$ = state.select(queryJeSelectionHighlightInvalid);
    this.editable$ = state.select(queryJeEditable);
    this.isFormulaEmpty$ = this.formula$.pipe(map$(isFormulaEmpty));

    this.componentSubscription = component$.subscribe((component: any) => {
      this.component = component;
    });
  }

  ngOnDestroy(): void {
    this.componentSubscription.unsubscribe();
  }

  onTest(event: FormulaTestEvent): void {
    this.commands.testComponentFormula(event, getJeComponentId(this.component));
  }

  onFormulaChange(formula: Formula): void {
    const componentId = getJeComponentId(this.component);
    if (componentId) {
      this.commands.componentSetField(componentId, 'formula', formula);
    }
  }

  onMemoChange(memo: string): void {
    const componentId = getJeComponentId(this.component);
    if (componentId) {
      this.commands.componentSetField(componentId, 'memo', memo);
    }
  }

  onSelectedTabChange(name: string): void {
    this.commands.tabSelect(name);
  }

  goToItem(item: JeItem): void {
    const componentId = getJeComponentId(this.component);

    if (componentId && item && item.costCenterId) {
      this.commands.costCenterItemSelect(item.costCenterId, item.jobItemId);
    }
  }

  onItemsGridCellDoubleClicked(event: CellDoubleClickedEvent<JeItem>): void {
    if (event.data) {
      this.goToItem(event.data);
    }
  }

  onContextItemChange(item: JeItem): void {
    this.contextItem = item;
  }
}
